import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === Per-theme override (simple, user-explicit) ===
const _getActiveTheme = () => {
  try { return localStorage.getItem('rl_dashboard_theme') || 'glass'; } catch { return 'glass'; }
};
const _applyThemeOverrides = (b, themeName) => {
  if (!b || !b.theme_overrides) return b;
  const t = themeName || _getActiveTheme();
  const ov = b.theme_overrides[t];
  if (!ov || typeof ov !== 'object') return b;
  const merged = { ...b };
  Object.keys(ov).forEach((k) => {
    if (ov[k] !== undefined && ov[k] !== null && ov[k] !== '') merged[k] = ov[k];
  });
  return merged;
};





// === Branding cache to prevent logo flash on refresh (FOUC fix) ===
const BRANDING_CACHE_KEY = 'rl_branding_cache_v1';
const _getCachedBranding = () => {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
};
const _saveBrandingCache = (b) => {
  try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(b)); } catch {}
};
const _clearBrandingCache = () => {
  try { localStorage.removeItem(BRANDING_CACHE_KEY); } catch {}
};

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

// Default branding for Tenant Admin
const defaultBranding = {
  logo_url: '/radiuslink-logo.png',
  logo_portal_url: null,
  logo_invoice_url: null,
  logo_scale: 1.0,
  primary_color: '#3B82F6',  // Blue
  secondary_color: '#06B6D4', // Cyan
  company_name: 'RADIUSLINK',
  website: null,
  tagline: 'ISP Billing Management System',
  phone: null,
  email: null,
  address: null,
  // Black sidebar theme (for tenants without white label)
  sidebar_color: '#000000',
  sidebar_gradient_start: '#000000',
  sidebar_gradient_end: '#1a1a1a',
  sidebar_use_gradient: false,
  // Light content background
  content_bg_color: '#f8fafc',
  content_bg_gradient_start: '#f0f9ff',
  content_bg_gradient_end: '#e0f2fe',
  content_use_gradient: true,
  // Black header (for tenants without white label)
  header_color: '#000000',
  header_gradient_start: '#000000',
  header_gradient_end: '#1a1a1a',
  header_use_gradient: false
};

// Super Admin exclusive branding - dark professional theme
const superAdminBranding = {
  logo_url: '/radiuslink-logo.png', // Will use RADIUSLINK logo
  logo_scale: 1.0,
  primary_color: '#6366f1', // Indigo
  secondary_color: '#22c55e', // Green
  company_name: 'RADIUSLINK',
  website: null,
  tagline: 'ISP Billing Management System',
  phone: null,
  email: null,
  address: null,
  // Dark sidebar
  sidebar_color: '#000000',
  sidebar_gradient_start: '#000000',
  sidebar_gradient_end: '#000000',
  sidebar_use_gradient: true,
  // Dark content background with gradient
  content_bg_color: '#111827',
  content_bg_gradient_start: '#111827',
  content_bg_gradient_end: '#1f2937',
  content_use_gradient: true,
  // Header with better contrast for icons - using indigo/purple gradient
  header_color: '#4f46e5',
  header_gradient_start: '#6366f1',
  header_gradient_end: '#8b5cf6',
  header_use_gradient: true,
  // Flag to identify Super Admin theme
  is_super_admin: true
};

// Helper function to get user role from token
const getUserRoleFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    // Decode JWT payload (base64)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role?.toLowerCase();
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(() => _getCachedBranding() || defaultBranding);
  const [loading, setLoading] = useState(true);
  const [lastToken, setLastToken] = useState(localStorage.getItem('token'));
  // Prevents duplicate concurrent branding fetches (multiple useEffects + events can fire close together)
  const inFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);

  // Load branding on mount
  useEffect(() => {
    loadBranding();
  }, []);

  // Listen for login/logout events to refresh branding
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        const newToken = e.newValue;
        if (newToken !== lastToken) {
          setLastToken(newToken);
          if (newToken) {
            // Token added (login) - reload branding
            console.log('[BrandingContext] Token changed, reloading branding...');
            loadBranding();
          } else {
            // Token removed (logout) - reset to default
            console.log('[BrandingContext] Token removed, resetting branding...');
            _clearBrandingCache();
            setBranding(defaultBranding);
          }
        }
      }
    };

    // Listen for custom login event
    const handleLoginEvent = async () => {
      console.log('[BrandingContext] Login event detected, reloading branding...');
      // Multiple retries with increasing delays to ensure token is saved and API is ready
      const delays = [200, 500, 1000];
      for (const delay of delays) {
        await new Promise(resolve => setTimeout(resolve, delay));
        const token = localStorage.getItem('token');
        if (token) {
          console.log(`[BrandingContext] Token found after ${delay}ms, loading branding...`);
          await loadBranding();
          break;
        }
      }
    };

    // Listen for branch change event (may need different branding)
    const handleBranchChange = () => {
      console.log('[BrandingContext] Branch changed, reloading branding...');
      loadBranding();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleLoginEvent);
    window.addEventListener('resetBranch', handleBranchChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleLoginEvent);
      window.removeEventListener('resetBranch', handleBranchChange);
    };
  }, [lastToken]);


  // 2026-02: Cek custom-domain branding (mis. app.kumarahotspot.com).
  // Jika hostname terdaftar di tenants.custom_domain → tenant punya
  // branding kustom sendiri yang harus diapply pre-login.
  const fetchDomainBranding = async () => {
    try {
      const host = (window.location.hostname || '').toLowerCase();
      if (!host || host === 'localhost' || /^[0-9.]+$/.test(host)) return null;
      const response = await axios.get(
        `${API_URL}/api/public/branding-by-host`,
        { params: { host }, timeout: 4000 }
      );
      if (response.data?.success && response.data?.data) {
        const d = response.data.data;
        console.log('[BrandingContext] Custom domain branding loaded:', d.tenant_name);
        // Apply <title>
        try { if (d.app_title) document.title = d.app_title; } catch {}
        // Apply favicon (kalau logo BASE64/URL tersedia)
        try {
          const ico = d.favicon_url || d.logo_url;
          if (ico) {
            let link = document.querySelector("link[rel*='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = ico;
          }
        } catch {}
        return d;
      }
    } catch (error) {
      console.warn('[BrandingContext] domain-branding fetch failed:', error?.message);
    }
    return null;
  };

  // Fetch default branding from server (for non-white-label tenants).
  // Prioritas: custom domain → server default → hardcoded.
  const fetchDefaultBranding = async () => {
    // Cek dulu custom domain branding
    const domainBranding = await fetchDomainBranding();
    if (domainBranding) return domainBranding;

    try {
      const response = await axios.get(`${API_URL}/api/public/default-branding`);
      if (response.data.success && response.data.data) {
        console.log('[BrandingContext] Fetched default branding from server');
        return response.data.data;
      }
    } catch (error) {
      console.log('[BrandingContext] Using hardcoded defaults');
    }
    return null;
  };

  const loadBranding = async () => {
    // Dedup: skip if a fetch is already running OR if last fetch was <800ms ago
    if (inFlightRef.current) return;
    const nowTs = Date.now();
    if (nowTs - lastFetchAtRef.current < 800) return;
    inFlightRef.current = true;
    lastFetchAtRef.current = nowTs;
    try {
      // Fetch default branding settings from server first
      const serverDefaults = await fetchDefaultBranding() || {};
      const sd = serverDefaults; // Server defaults shorthand (available to both super_admin and tenant branches)
      
      const token = localStorage.getItem('token');
      if (!token) {
        // 2026-02: Apply custom-domain branding even pre-login.
        // Kalau hostname terdaftar di tenants.custom_domain, brand harus
        // diterapkan sebelum user login agar konsisten dengan domain pemilik.
        if (serverDefaults && serverDefaults.tenant_name) {
          setBranding((prev) => ({ ...prev, ...serverDefaults }));
        }
        setLoading(false);
        return;
      }

      // Check user role first
      const userRole = getUserRoleFromToken();
      console.log('[BrandingContext] User role:', userRole);

      // If Super Admin, load Super Admin branding from API
      if (userRole === 'super_admin' || userRole === 'superadmin') {
        console.log('[BrandingContext] Super Admin detected, loading Super Admin branding from API');
        try {
          const response = await axios.get(`${API_URL}/api/v2/superadmin/branding`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.data.success) {
            const data = response.data.data;
            const superAdminBrandingFromAPI = {
              logo_url: data.logo_url || data.logo_portal_url || sd.logo_url || '/radiuslink-logo.png',
              logo_scale: data.logo_scale || 1.5,
              primary_color: data.primary_color || '#6366f1',
              secondary_color: data.secondary_color || '#22c55e',
              company_name: data.company_name || sd.company_name || 'RADIUSLINK',
              website: data.website || null,
              tagline: data.tagline || 'ISP Billing Management System',
              phone: data.phone || null,
              email: data.email || null,
              address: data.address || null,
              sidebar_color: data.sidebar_color || sd.sidebar_color || '#000000',
              sidebar_gradient_start: data.sidebar_gradient_start || sd.sidebar_gradient_start || '#000000',
              sidebar_gradient_end: data.sidebar_gradient_end || '#000000',
              sidebar_use_gradient: data.sidebar_use_gradient !== false,
              content_bg_color: data.content_bg_color || '#111827',
              content_bg_gradient_start: data.content_bg_gradient_start || '#111827',
              content_bg_gradient_end: data.content_bg_gradient_end || '#1f2937',
              content_use_gradient: data.content_use_gradient !== false,
              header_color: data.header_color || '#6366f1',
              header_gradient_start: data.header_gradient_start || '#6366f1',
              header_gradient_end: data.header_gradient_end || '#8b5cf6',
              header_use_gradient: data.header_use_gradient !== false,
              is_super_admin: true
            };
            setBranding(superAdminBrandingFromAPI);
            _saveBrandingCache(superAdminBrandingFromAPI);
            applyTheme(superAdminBrandingFromAPI);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('[BrandingContext] Error loading Super Admin branding, using defaults:', error);
          setBranding(superAdminBranding);
          applyTheme(superAdminBranding);
          setLoading(false);
          return;
        }
      }

      // For Tenant Admin and other roles, load tenant branding from API
      const response = await axios.get(`${API_URL}/api/v2/tenant/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data || {};  // Ensure data is not null
        const tenantBranding = {
          logo_url: data.logo_url || data.logo_portal_url || sd.logo_url || '/radiuslink-logo.png',
          logo_portal_url: data.logo_portal_url || null,
          logo_invoice_url: data.logo_invoice_url || null,
          logo_scale: data.logo_scale || 1.5,
          primary_color: data.primary_color || '#4F46E5',
          secondary_color: data.secondary_color || '#10B981',
          company_name: data.company_name || '',
          website: data.website || null,
          tagline: data.tagline || null,
          phone: data.phone || null,
          email: data.email || null,
          address: data.address || null,
          // Sidebar customization
          sidebar_color: data.sidebar_color || sd.sidebar_color || '#000000',
          sidebar_gradient_start: data.sidebar_gradient_start || sd.sidebar_gradient_start || '#000000',
          sidebar_gradient_end: data.sidebar_gradient_end || sd.sidebar_gradient_end || '#1a1a1a',
          sidebar_use_gradient: data.sidebar_use_gradient || false,
          // Content/Dashboard background
          content_bg_color: data.content_bg_color || '#f8fafc',
          content_bg_gradient_start: data.content_bg_gradient_start || '#f8fafc',
          content_bg_gradient_end: data.content_bg_gradient_end || '#e2e8f0',
          content_use_gradient: data.content_use_gradient !== false,
          // Header/Navbar customization
          header_color: data.header_color || sd.header_color || '#2563eb',
          header_gradient_start: data.header_gradient_start || sd.header_gradient_start || '#3b82f6',
          header_gradient_end: data.header_gradient_end || sd.header_gradient_end || '#0ea5e9',
          header_use_gradient: data.header_use_gradient !== false,
          // Text colors
          primary_text_color: data.primary_text_color || null,
          sidebar_text_color: data.sidebar_text_color || null,
          header_text_color: data.header_text_color || null,
          // Per-theme overrides — CRITICAL: must be preserved here so _applyThemeOverrides()
          // can merge the active theme's per-theme colors over the root values before
          // exposing the branding object to consumers (Layout, Dashboard CSS vars, etc.).
          theme_overrides: (data.theme_overrides && typeof data.theme_overrides === 'object') ? data.theme_overrides : {},
          // Flag
          is_super_admin: false
        };
        
        setBranding(tenantBranding);
        _saveBrandingCache(tenantBranding);
        // Apply CSS variables using the EFFECTIVE (per-theme-merged) branding so dashboard
        // accent colors track the selected theme correctly.
        applyTheme(_applyThemeOverrides(tenantBranding));
      }
    } catch (error) {
      console.error('Error loading branding:', error);
      // On error, check if super admin and use their branding
      const userRole = getUserRoleFromToken();
      if (userRole === 'super_admin' || userRole === 'superadmin') {
        setBranding(superAdminBranding);
        applyTheme(superAdminBranding);
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const applyTheme = (brandingData) => {
    const root = document.documentElement;
    
    if (brandingData.primary_color) {
      root.style.setProperty('--primary-color', brandingData.primary_color);
    }
    
    if (brandingData.secondary_color) {
      root.style.setProperty('--secondary-color', brandingData.secondary_color);
    }
  };

  const refreshBranding = () => {
    loadBranding();
  };

  // Track current dashboard theme in state so effective branding can react instantly.
  // We seed from localStorage and update SYNCHRONOUSLY via 'rl-theme-change' event detail
  // (avoids race with useDashboardTheme's useEffect that writes localStorage after render).
  const [currentTheme, setCurrentTheme] = useState(() => _getActiveTheme());
  useEffect(() => {
    const h = (e) => {
      const detail = (e && e.detail) || {};
      if (detail.theme !== undefined && detail.theme !== null) {
        setCurrentTheme(detail.theme);
      } else {
        setCurrentTheme(_getActiveTheme());
      }
    };
    window.addEventListener('rl-theme-change', h);
    return () => window.removeEventListener('rl-theme-change', h);
  }, []);
  const _effective = _applyThemeOverrides(branding, currentTheme);
  // Re-apply CSS variables (primary-color, secondary-color) whenever the effective branding
  // changes (initial load, theme switch, refresh after save) so accent colors update instantly.
  useEffect(() => {
    if (_effective && (_effective.primary_color || _effective.secondary_color)) {
      applyTheme(_effective);
    }
  }, [_effective.primary_color, _effective.secondary_color, currentTheme]);

  // Auto-refresh branding from API whenever the user switches dashboard theme.
  // This ensures any per-theme override saved in another tab or via SQL is reflected
  // immediately. The dedup guard inside loadBranding (inFlightRef + 800ms cooldown)
  // protects against spam if user rapidly toggles themes.
  // We skip the very first render (initial mount already triggers loadBranding via the
  // mount effect at the top), so this only runs on theme changes after mount.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    loadBranding();
  }, [currentTheme]);

  return (
    <BrandingContext.Provider value={{ branding: _effective, rawBranding: branding, currentTheme, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
